import * as React from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

import { cn } from "../../lib/utils";

export type SortDirection = "asc" | "desc" | null;

export interface SortableHeaderProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /** Whether this column is sortable */
  sortable?: boolean;
  /** Current sort direction for this column */
  sortDirection?: SortDirection;
  /** Callback when sort is requested */
  onSort?: () => void;
  /** Text alignment */
  align?: "left" | "center" | "right";
}

/**
 * SortableHeader component for data tables
 *
 * @example
 * // Sortable header
 * <SortableHeader
 *   sortable
 *   sortDirection={sortState.column === 'name' ? sortState.direction : null}
 *   onSort={() => handleSort('name')}
 * >
 *   Namn
 * </SortableHeader>
 *
 * // Non-sortable header
 * <SortableHeader>Åtgärder</SortableHeader>
 */
const SortableHeader = React.forwardRef<
  HTMLTableCellElement,
  SortableHeaderProps
>(
  (
    {
      className,
      sortable = false,
      sortDirection,
      onSort,
      align = "left",
      children,
      ...props
    },
    ref,
  ) => {
    const isSorted = sortDirection !== null && sortDirection !== undefined;

    return (
      <th
        ref={ref}
        className={cn(
          "p-4 text-left text-xs uppercase tracking-wider text-muted-foreground font-medium",
          sortable &&
            "cursor-pointer select-none hover:bg-muted/50 transition-colors",
          className,
        )}
        onClick={sortable ? onSort : undefined}
        aria-sort={
          isSorted
            ? sortDirection === "asc"
              ? "ascending"
              : "descending"
            : undefined
        }
        {...props}
      >
        <div
          className={cn(
            "flex items-center gap-1",
            align === "right" && "justify-end",
            align === "center" && "justify-center",
          )}
        >
          <span>{children}</span>
          {sortable && (
            <span className="text-muted-foreground">
              {isSorted ? (
                sortDirection === "asc" ? (
                  <ChevronUp size={14} className="text-primary" />
                ) : (
                  <ChevronDown size={14} className="text-primary" />
                )
              ) : (
                <ChevronsUpDown size={14} className="opacity-50" />
              )}
            </span>
          )}
        </div>
      </th>
    );
  },
);
SortableHeader.displayName = "SortableHeader";

export { SortableHeader };
